<template>
    <div>
        <div
            :class="[
                'rounded-md ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset',
                error
                    ? 'ring-red-300 focus-within:ring-red-500'
                    : 'ring-taupe-300 focus-within:ring-sky-600',
                disabled ? 'opacity-60' : '',
            ]"
        >
            <div ref="editorRoot" class="zen-json-editor" />
        </div>
        <div class="mt-1 flex items-center justify-between gap-2">
            <p v-if="error" class="text-sm text-red-700">{{ error }}</p>
            <p v-else-if="warning" class="text-sm text-amber-600">
                {{ warning }}
            </p>
            <span v-else />
            <button
                type="button"
                class="text-sm font-medium text-sky-600 hover:text-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="disabled || !!error"
                @click="format"
            >
                Format
            </button>
        </div>
    </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Compartment, EditorState } from '@codemirror/state'
import { EditorView, basicSetup } from 'codemirror'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { linter, lintGutter } from '@codemirror/lint'

const props = defineProps({
    // Typed nullable because the caller may bind this to a data property
    // that starts as `null` (e.g. a "create new value" form field) — Vue
    // only substitutes `default` for an `undefined` value, not an explicit
    // `null`, so `null` genuinely reaches this component at runtime.
    /** @type {import('vue').PropType<string|null>} */
    modelValue: {
        type: String,
        default: '',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    id: {
        type: String,
        default: null,
    },
    // Mirror config/zenmanage.php's flag_json_value_max_bytes /
    // flag_json_value_max_depth (ZEN-721) so the editor warns/rejects
    // client-side before a round trip confirms the same limits server-side
    // in FlagValueService::validateJsonDisplay().
    maxBytes: {
        type: Number,
        default: 65536,
    },
    maxDepth: {
        type: Number,
        default: 10,
    },
})

const emit = defineEmits(['update:modelValue', 'update:valid'])

// A value is flagged as "approaching" a limit once it crosses this fraction
// of maxBytes, or once its nesting depth is within one level of maxDepth.
const WARNING_BYTES_THRESHOLD = 0.9

const editorRoot = ref(null)
/** @type {import('vue').Ref<string|null>} */
const error = ref(null)
/** @type {import('vue').Ref<string|null>} */
const warning = ref(null)

// Never read in the template, so a plain closure variable (rather than a
// ref) is the right call here — same pattern as BannerCarousel.vue's
// `timer` handle. Reassigned to null in onBeforeUnmount(); checked before
// use in the watch handler and format() below.
/** @type {EditorView | null} */
let view = null
const editableCompartment = new Compartment()

// Instantiated once at module scope rather than per call — this is invoked on every
// editor keystroke, and TextEncoder instances are cheap to reuse but not free to
// allocate repeatedly.
// eslint-disable-next-line no-undef -- TextEncoder is a standard browser global; this project's eslint config doesn't declare it, but it's available in every target runtime (all browsers this app supports, plus jsdom in tests).
const textEncoder = new TextEncoder()

/** UTF-8 byte length, matching PHP's strlen() on the same string server-side. */
function byteSizeOf(text) {
    return textEncoder.encode(text).length
}

/**
 * Nesting depth of a decoded JSON value, matching FlagValueService's
 * jsonNestingDepth(): a scalar is 0, an empty array/object is 1, and a
 * non-empty container is 1 + the deepest nesting among its children.
 *
 * Uses reduce() rather than `Math.max(...children.map(...))` — spreading a
 * large sibling array onto Math.max() as call arguments can blow the call
 * stack, where reduce() stays a constant-size accumulation regardless of
 * how many siblings a container has.
 */
function jsonNestingDepth(value) {
    if (value === null || typeof value !== 'object') {
        return 0
    }

    const children = Array.isArray(value) ? value : Object.values(value)
    if (children.length === 0) {
        return 1
    }

    return (
        1 +
        children.reduce(
            (deepest, child) => Math.max(deepest, jsonNestingDepth(child)),
            0
        )
    )
}

/**
 * Mirrors the API's validateJsonDisplay(): a JSON.parse() failure, an
 * over-size display, or over-deep nesting are all rejected here exactly as
 * they are server-side, so client-side gating matches server-side rejection.
 * Returns `{ error, warning }` — `error` blocks save; `warning` (only set
 * when there's no error) flags a value that's within limits but close to
 * one of them.
 */
function evaluate(text) {
    if (text.trim() === '') {
        return { error: 'Value must not be blank.', warning: null }
    }

    // Byte size before JSON.parse(): an over-limit paste is rejected without ever
    // being parsed, same ordering as the API's validateJsonDisplay() (ZEN-721).
    const byteSize = byteSizeOf(text)
    if (byteSize > props.maxBytes) {
        return {
            error: `Value must not exceed ${props.maxBytes} bytes (got ${byteSize}).`,
            warning: null,
        }
    }

    let parsed
    try {
        parsed = JSON.parse(text)
    } catch (parseException) {
        return { error: parseException.message, warning: null }
    }

    const depth = jsonNestingDepth(parsed)
    if (depth > props.maxDepth) {
        return {
            error: `Value must not be nested more than ${props.maxDepth} levels deep (got ${depth}).`,
            warning: null,
        }
    }

    if (byteSize >= props.maxBytes * WARNING_BYTES_THRESHOLD) {
        return {
            error: null,
            warning: `Approaching the ${props.maxBytes}-byte size limit (${byteSize} bytes so far).`,
        }
    }

    if (props.maxDepth > 1 && depth >= props.maxDepth - 1) {
        return {
            error: null,
            warning: `Approaching the ${props.maxDepth}-level nesting limit (${depth} levels so far).`,
        }
    }

    return { error: null, warning: null }
}

function setValidity(text) {
    const result = evaluate(text)
    error.value = result.error
    warning.value = result.warning
    emit('update:valid', !error.value)
}

onMounted(() => {
    const updateListener = EditorView.updateListener.of((update) => {
        if (update.docChanged) {
            const text = update.state.doc.toString()
            emit('update:modelValue', text)
            setValidity(text)
        }
    })

    view = new EditorView({
        parent: editorRoot.value,
        state: EditorState.create({
            doc: props.modelValue ?? '',
            extensions: [
                basicSetup,
                json(),
                linter(jsonParseLinter()),
                lintGutter(),
                editableCompartment.of(EditorView.editable.of(!props.disabled)),
                updateListener,
                // Applied to the actual focusable/editable content element
                // (not the outer wrapper div) so a `<label for>` pointing at
                // this id associates with the right element.
                ...(props.id
                    ? [EditorView.contentAttributes.of({ id: props.id })]
                    : []),
                EditorView.theme({
                    '&': { fontSize: '0.875rem' },
                    '.cm-content': {
                        padding: '0.5rem 0.75rem',
                        fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, monospace',
                    },
                    '.cm-scroller': { minHeight: '6rem' },
                    '&.cm-focused': { outline: 'none' },
                }),
            ],
        }),
    })

    setValidity(props.modelValue ?? '')
})

onBeforeUnmount(() => {
    view?.destroy()
    view = null
})

watch(
    () => props.modelValue,
    (value) => {
        const text = value ?? ''
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `view` is reassigned to an EditorView instance in onMounted() and back to null in onBeforeUnmount(), both in closures separate from this one; type-aware linters that don't track control flow across those boundaries can misread this as always-null.
        if (view && text !== view.state.doc.toString()) {
            view.dispatch({
                changes: {
                    from: 0,
                    to: view.state.doc.length,
                    insert: text,
                },
            })
            setValidity(text)
        }
    }
)

watch(
    () => props.disabled,
    (disabled) => {
        view?.dispatch({
            effects: editableCompartment.reconfigure(
                EditorView.editable.of(!disabled)
            ),
        })
    }
)

function format() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- see the identical note in the watch() handler above; `view` genuinely can be null here (before mount or after unmount).
    if (!view) {
        return
    }

    const text = view.state.doc.toString()
    let formatted
    try {
        formatted = JSON.stringify(JSON.parse(text), null, 4)
    } catch {
        return
    }

    view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: formatted },
    })
}

defineExpose({ format })
</script>
